/**
 * AvisoContatoExistente — "esse contato já existe aqui" (CRM-12).
 *
 * Consultado no onBlur de e-mail, telefone e CPF/CNPJ do Novo Lead, do
 * cadastro de cliente e do diálogo de conversão. É AVISO, não bloqueio:
 * telefone e e-mail se repetem em família e empresa (decisão do dono); o
 * CPF/CNPJ repetido é recusado pelo backend com mensagem no campo.
 *
 * `useContatoExistente(consultar)` — `consultar(params)` resolve com
 * `{clientes, leads}` (services/crm.possiveisDuplicados). A consulta é
 * silenciosa: falha vira lista vazia, nunca trava a tela.
 */

import React, { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { mascaraDocumento } from "../../utils/documento";

const VAZIO = { clientes: [], leads: [] };

const ROTULO_COINCIDE = {
  email: "mesmo e-mail",
  telefone: "mesmo telefone",
  documento: "mesmo CPF/CNPJ",
};

export function rotuloDaCoincidencia(coincide) {
  return (coincide || []).map((c) => ROTULO_COINCIDE[c] || c).join(", ");
}

export function useContatoExistente(consultar) {
  const [candidatos, setCandidatos] = useState(VAZIO);
  const ultimaConsulta = useRef(0);

  const verificar = useCallback(
    async (params) => {
      const { email = "", telefone = "", documento = "" } = params || {};
      const chamada = ultimaConsulta.current + 1;
      ultimaConsulta.current = chamada;
      if (!consultar || !(email.trim() || telefone.trim() || documento.trim())) {
        setCandidatos(VAZIO);
        return VAZIO;
      }
      let resultado = VAZIO;
      try {
        const resposta = await consultar(params);
        resultado = {
          clientes: Array.isArray(resposta?.clientes) ? resposta.clientes : [],
          leads: Array.isArray(resposta?.leads) ? resposta.leads : [],
        };
      } catch {
        // Aviso auxiliar: sem rede ou sem permissao, a tela segue sem ele.
        resultado = VAZIO;
      }
      // Resposta atrasada de um blur anterior nao sobrescreve a atual.
      if (ultimaConsulta.current === chamada) setCandidatos(resultado);
      return resultado;
    },
    [consultar]
  );

  const limpar = useCallback(() => setCandidatos(VAZIO), []);
  return { candidatos, verificar, limpar };
}

const Aviso = styled.div`
  border: 1px solid ${({ theme }) => theme.warningBorder};
  background: ${({ theme }) => theme.warningSurface};
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.textSecondary};
  display: grid;
  gap: 4px;

  strong {
    color: ${({ theme }) => theme.warningText};
  }
  ul {
    margin: 0;
    padding-left: 16px;
  }
  li {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  button {
    margin-left: 8px;
  }
`;

function descricaoDoCliente(c) {
  const doc = c.documento ? mascaraDocumento(c.documento) : "";
  return [c.codigo, c.nome, doc, c.telefone, c.email].filter(Boolean).join(" · ");
}

function descricaoDoLead(l) {
  return [l.numero, l.contato_nome || l.titulo, l.etapa_nome, l.responsavel_nome]
    .filter(Boolean)
    .join(" · ");
}

function AvisoContatoExistente({ candidatos, onVincularCliente, rotuloVincular = "Vincular" }) {
  const clientes = candidatos?.clientes || [];
  const leads = candidatos?.leads || [];
  if (!clientes.length && !leads.length) return null;
  return (
    <Aviso role="status" data-testid="aviso-contato-existente">
      <strong>Este contato já está cadastrado nesta unidade.</strong>
      {clientes.length > 0 && (
        <ul aria-label="Clientes com o mesmo contato">
          {clientes.map((c) => (
            <li key={c.id} title={descricaoDoCliente(c)}>
              Cliente <Link to={`/clientes/${c.id}`}>{descricaoDoCliente(c)}</Link>
              {" "}({rotuloDaCoincidencia(c.coincide)})
              {onVincularCliente && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => onVincularCliente(c)}
                >
                  {rotuloVincular}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {leads.length > 0 && (
        <ul aria-label="Leads abertos com o mesmo contato">
          {leads.map((l) => (
            <li key={l.id} title={descricaoDoLead(l)}>
              Lead aberto <Link to={`/crm/leads/${l.id}`}>{descricaoDoLead(l)}</Link>
              {" "}({rotuloDaCoincidencia(l.coincide)})
            </li>
          ))}
        </ul>
      )}
    </Aviso>
  );
}

export default AvisoContatoExistente;
